import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { LabelValueItem } from '@ru-digital-assets/ui-component';
import { format } from 'date-fns';
import AssetsDetailsWrapper from '../components/assets-details-wrapper/assets-details-wrapper';
import LoadDocBtn from '../modules/load-doc-btn';
import { useAppDispatch } from '../store';
import {
  getDecisionDoc,
  getIssue,
  setSigningInfo,
  signOperation,
} from '../store/actions/operations-sign';
import { setOperationResult } from '../store/actions/sign-result';
import { RootState } from '../types';
import currencyFormat from '../utils/currencyFormat';
import { downloadFile, getPdfFromBase64 } from '../utils/files/downloadFile';
import { getSignDocName } from '../utils/strings/getSignDocName';
import { getFormatDate } from '../utils/utils';
import { BlockchainTransactionInfo } from 'components';
import { useSign } from 'hooks';

const CfaReleaseSignContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const companyInfo = useSelector(
    (state: RootState) => state.companiesInfo.companies
  );
  const auth = useSelector((state: RootState) => state.auth.account);
  const decisionId = useSelector(
    (state: RootState) => state.operationsSign.data.releaseCfa.decisionId
  );
  const decisionDoc = useSelector(
    (state: RootState) => state.operationsSign.data.releaseCfa.decisionDoc
  );
  const decisionHash = useSelector(
    (state: RootState) => state.operationsSign.data.releaseCfa.decisionHash
  );
  const totalSum = useSelector(
    (state: RootState) => state.operationsSign.data.releaseCfa.claim
  );
  const loadingSign = useSelector(
    (state: RootState) => state.operationsSign.loading.releaseCfaSign
  );
  const issue = useSelector(
    (state: RootState) => state.operationsSign.data?.issue
  );
  const rightData = useSelector(
    (state: RootState) => state.operationsSign.data.newReleaseCfa
  );

  const {
    certificatesList,
    selectedCertificate,
    signEntity,
    onUserSelectCertificate,
    usersCertificates,
  } = useSign();

  const currentCert = useMemo(() => {
    return usersCertificates.find(
      (certificate) => certificate.thumbprint === selectedCertificate?.value
    );
  }, [usersCertificates, selectedCertificate]);

  useEffect(() => {
    if (decisionId) {
      dispatch(
        getDecisionDoc({
          id: decisionId,
          withHash: true,
        })
      );
      dispatch(getIssue({ id: decisionId }));
    }
  }, [decisionId]);

  const onClose = () => history.push('/assets');

  const onSubmit = async () => {
    if (currentCert && decisionHash && decisionId) {
      const { pdfSigned, signed } = await signEntity(decisionHash);
      if (pdfSigned && signed) {
        dispatch(
          setSigningInfo({
            thumbprint: currentCert.thumbprint,
            name: currentCert.issuerName,
            date: `с ${format(
              new Date(currentCert.validFrom),
              'dd/MM/yyyy'
            )} до ${format(new Date(currentCert.validTo), 'dd/MM/yyyy')}`,
          })
        );
        dispatch(
          signOperation({
            docType: 'SIGNED_DECISION',
            signature: pdfSigned,
            hash: decisionHash,
            issuingDecisionId: decisionId,
          })
        )
          .then((action) => {
            const resultStatus = action.meta.requestStatus === 'fulfilled';
            dispatch(
              setOperationResult({ name: 'releaseCfa', status: resultStatus })
            );
            history.push(
              `/assets/cfa-info/${(action.payload as { id: number })?.id}`,
              { id: (action.payload as { id: number })?.id }
            );
          })
          .catch(() => history.push('/cfa/sign-result'));
      }
    }
  };

  const decisionDocs = useMemo(() => {
    const docsBtns: any = [];

    if (decisionDoc && decisionId) {
      docsBtns.push(
        <LoadDocBtn
          title={getSignDocName(issue?.id || 0)}
          onSpecDownload={() => {
            const resultBlob = getPdfFromBase64(decisionDoc);
            downloadFile(
              resultBlob,
              getSignDocName(issue?.id || 0),
              'application/pdf'
            );
          }}
        />
      );
    }

    return docsBtns;
  }, [decisionDoc, decisionId, issue?.status]);

  return (
    <AssetsDetailsWrapper
      isRelease
      onClose={onClose}
      onSubmit={onSubmit}
      totalSum={totalSum}
      onCertSelect={onUserSelectCertificate}
      selectedCert={selectedCertificate}
      options={certificatesList}
      isDisabled={
        !selectedCertificate ||
        loadingSign ||
        !decisionDoc ||
        !decisionHash ||
        !decisionId
      }
      fromName="Эмитент:"
      toName="Приобретатель:"
      to={{
        name: issue?.recipient || '-',
        inn: issue?.recipientTIN || '-',
        hash: issue?.recipientBlockchainAddress || '-',
      }}
      from={
        companyInfo[auth.id]
          ? {
              name: `“${companyInfo[auth.id].shortName}“`,
              inn: companyInfo[auth.id].tin,
              hash: issue?.issuerBlockchainAddress || '-',
            }
          : undefined
      }
      docs={decisionDocs}
      rightContent={
        <div className="assets-details__col assets-details__col-12 cfa-release-right">
          <h2 className="text-2 assets-details__title">Параметры выпуска:</h2>
          <div className="assets-details__item">
            <LabelValueItem
              title="Количество выпускаемых токенов"
              text={rightData?.tokenCount}
            />
          </div>
          <div className="assets-details__item">
            <LabelValueItem
              title="Денежное требование по токену"
              text={currencyFormat(issue?.tokenPrice || 0)}
            />
          </div>
          <div className="assets-details__item">
            <LabelValueItem
              title="Общий объем денежных требований"
              text={currencyFormat(
                issue !== undefined ? issue?.tokenCount * issue?.tokenPrice : 0
              )}
            />
          </div>
          <div className="assets-details__item">
            <LabelValueItem
              title="Срок исполнения требований"
              text={getFormatDate(
                rightData?.expiryDate || new Date(Date.now()),
                'dd.MM.yyyy',
                true
              )}
            />
          </div>
          {issue?.issuePaymentDetails?.paymentType?.code === 'FIAT' && (
            <>
              <div className="assets-details__item">
                <LabelValueItem
                  title="Сумма для оплаты"
                  text={currencyFormat(rightData?.claim || 100)}
                />
              </div>
            </>
          )}
          {issue?.blockchainTransactionHash && (
            <>
              <p className="p p_font-size_12 p_color_light-grey">
                ID транзакции
              </p>
              <div className="mb_32">
                <BlockchainTransactionInfo
                  hash={issue?.blockchainTransactionHash}
                />
              </div>
            </>
          )}
          {issue?.issuePaymentDetails?.paymentType?.code !== 'FIAT' && (
            <>
              <h2 className="text-2 assets-details__title">
                Встречное предоставление:
              </h2>
              <div className="assets-details__item">
                <p className="p p_font-size_12 p_color_light-grey">
                  Наименование токенов, передаваемых за выпуск
                </p>
                <a href="/#">{issue?.issuePaymentDetails?.paymentType?.name}</a>
              </div>
              <div className="assets-details__item">
                <LabelValueItem
                  title="Количество токенов, передаваемых за выпуск"
                  text={issue?.issuePaymentDetails?.paymentTokenCount || 0}
                />
              </div>
            </>
          )}
          {issue?.issuePaymentDetails?.paymentType?.code === 'FIAT' && (
            <>
              <h2 className="text-2 assets-details__title">
                Реквизиты платежа
              </h2>
              <div className="assets-details__item">
                <LabelValueItem
                  title=""
                  text={
                    <div>
                      {issue?.issuer}
                      <br />
                      <br />
                      ИНН: {issue?.issuerTIN || '-'}
                      <br />
                      КПП: {issue?.issuePaymentDetails.kpp || '-'}
                      <br />
                      ОГРН: {issue?.issuePaymentDetails.regNumber || '-'}
                      <br />
                      Адрес: {issue?.issuePaymentDetails.address || '-'}
                      <br />
                      <br />
                      Р/С:{' '}
                      {issue?.issuePaymentDetails.bankAccountInfo
                        ?.accountNumber || '-'}
                      <br />
                      Банк:{' '}
                      {issue?.issuePaymentDetails.bankAccountInfo?.bankName ||
                        '-'}
                      <br />
                      БИК:{' '}
                      {issue?.issuePaymentDetails.bankAccountInfo?.bik || '-'}
                      <br />
                      К/С:{' '}
                      {issue?.issuePaymentDetails.bankAccountInfo
                        ?.corrAccountNumber || '-'}
                    </div>
                  }
                />
              </div>
              <div className="assets-details__item">
                <LabelValueItem
                  title="Основание платежа:"
                  text={`Оплата выпуска ЦФА "${
                    issue?.tokenName
                  }" по решению № ${issue?.id} от ${getFormatDate(
                    issue?.initialDate,
                    undefined,
                    true
                  )}. Без НДС
              `}
                />
              </div>
            </>
          )}
        </div>
      }
    />
  );
};

export default CfaReleaseSignContainer;
